import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import visualsService from "../../../../services/visualsService";
import {
  mfaDeptRequest,
  mfaDeptSuccess,
  mfaDeptFailure,
} from "../../../../store/slices/visualSlice";
import "./MFARegistrationByDepartment.css";
import { Loading } from "../../../common";
import { useTheme } from "../../../../context/ThemeContext";

const ITEMS_PER_PAGE = 5;

const COLORS = {
  excellent: "#06b6a4",
  good: "#6366f1",
  warning: "#f59e0b",
  critical: "#f43f5e",
};

const getComplianceColor = (percentage) => {
  if (percentage >= 95) return COLORS.excellent;
  if (percentage >= 75) return COLORS.good;
  if (percentage >= 40) return COLORS.warning;
  return COLORS.critical;
};

const getComplianceLabel = (percentage) => {
  if (percentage >= 95) return "Excellent";
  if (percentage >= 75) return "Good";
  if (percentage >= 40) return "Warning";
  return "Critical";
};

const MFARegistrationByDepartment = () => {
  const dispatch = useDispatch();

  const { isDark } = useTheme();

  // ✅ Get state from Redux slice
  const { loading, data: apiResponse, error } = useSelector((state) => state.mfaDept);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterLevel, setFilterLevel] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  // ✅ Dispatch slice actions
  useEffect(() => {
    const fetchData = async () => {
      dispatch(mfaDeptRequest());

      try {
        const result = await visualsService.getdepartmentWiseMfa();
        // console.log("result: ", result);

        if (result.success) {
          dispatch(mfaDeptSuccess(result));
        } else {
          dispatch(mfaDeptFailure(result.error || "Failed to load data"));
        }
      } catch (err) {
        dispatch(mfaDeptFailure(err.message || "Something went wrong"));
      }
    };

    // Only fetch if not already loaded
    if (!apiResponse) {
      fetchData();
    }
  }, [dispatch, apiResponse]);

  const distribution = apiResponse?.data?.distribution || [];
  const totalHumanIdentities = apiResponse?.data?.total_human_identities || 0;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortOrder, filterLevel]);

  const stats = useMemo(() => {
    if (!distribution.length) {
      return { overall: 0, byLevel: {} };
    }

    const totalRegistered = distribution.reduce(
      (sum, item) => sum + (item.registered_mfa_users || 0),
      0
    );
    const totalUsers = distribution.reduce(
      (sum, item) => sum + (item.total_users || 0),
      0
    );
    const overall = totalUsers > 0 ? Math.round((totalRegistered / totalUsers) * 100) : 0;

    const byLevel = { excellent: 0, good: 0, warning: 0, critical: 0 };
    distribution.forEach((item) => {
      const p = item.mfa_registration_percentage;
      if (p >= 95) byLevel.excellent++;
      else if (p >= 75) byLevel.good++;
      else if (p >= 40) byLevel.warning++;
      else byLevel.critical++;
    });

    return { overall, byLevel };
  }, [distribution]);

  const filteredData = useMemo(() => {
    let data = distribution.map((item) => ({
      name: item.department ? item.department.trim() : "Unassigned",
      percentage: parseFloat((item.mfa_registration_percentage || 0).toFixed(1)),
      registered: item.registered_mfa_users,
      total: item.total_users,
    }));

    if (searchTerm.trim()) {
      data = data.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterLevel !== "all") {
      data = data.filter((item) => {
        if (filterLevel === "excellent") return item.percentage >= 95;
        if (filterLevel === "good") return item.percentage >= 75 && item.percentage < 95;
        if (filterLevel === "warning") return item.percentage >= 40 && item.percentage < 75;
        if (filterLevel === "critical") return item.percentage < 40;
        return true;
      });
    }

    data.sort((a, b) =>
      sortOrder === "desc" ? b.percentage - a.percentage : a.percentage - b.percentage
    );

    return data;
  }, [distribution, searchTerm, sortOrder, filterLevel]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, currentPage]);

  const chartOptions = useMemo(() => {
    const categories = paginatedData.map((d) => d.name);
    const seriesData = paginatedData.map((d) => ({
      y: d.percentage,
      color: getComplianceColor(d.percentage),
      registered: d.registered,
      total: d.total,
    }));

    return {
      chart: {
        type: "bar",
        height: 280,
        backgroundColor: "transparent",
        style: { fontFamily: "inherit" },
        spacing: [5, 10, 5, 5],
      },
      title: { text: null },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        categories: categories,
        labels: {
          style: { fontSize: "11px", color: "#76859b", fontWeight: "500" },
        },
        lineColor: "#e2e8f0",
        tickColor: "#e2e8f0",
      },
      yAxis: {
        min: 0,
        max: 100,
        title: { text: null },
        labels: {
          format: "{value}%",
          style: { fontSize: "10px", color: "#76859b" },
        },
        gridLineColor: "transparent",

      },
      tooltip: {
        backgroundColor:  isDark ? "#1b3157" : "#ffffff",
        borderColor:  isDark ? "#233554" : "#e2e8f0",
        borderWidth: 1,
        borderRadius: 8,
        shadow: { color: "rgba(0,0,0,0.08)", offsetX: 0, offsetY: 3, width: 10 },
        useHTML: true,
        formatter: function () {
          const point = this.point;
          return `
            <div style="padding: 4px;">
              <div style="font-weight: 700; color: ${isDark ? "#E6F1FF" : "#1e293b"}; margin-bottom: 4px; font-size: 12px;">${this.x}</div>
              <div style="font-size: 11px; color:${isDark ? "#a1a3a9" : "#64748b"}; line-height: 1.5;">
                <b style="color:  ${isDark ? "#E6F1FF" : "#1e293b"}">${point.registered}/${point.total}</b> users<br/>
                MFA: <b style="color: ${point.color};">${this.y}%</b> (${getComplianceLabel(this.y)})
              </div>
            </div>
          `;
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 5,
          borderWidth: 0,
          pointPadding: 0.1,
          groupPadding: 0.05,
          dataLabels: {
            enabled: true,
            inside: true,
            align: "center",
            verticalAlign: "middle",
            format: "{y}%",
            style: {
              fontSize: "15px",
              fontWeight: "800",
              color: "#ffffff",
              textOutline: "none",
              textShadow: "0 1px 2px rgba(0,0,0,0.2)",
            },
            crop: false,
            overflow: "allow",
          },
        },
      },
      series: [{ name: "MFA Rate", data: seriesData }],
    };
  }, [paginatedData, isDark]);

  if (loading) {
    return (
      <div className="mfa-compact-card">
        <Loading text="Loading"/>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mfa-compact-card">
        <div className="mfa-state-message mfa-state-error">Error: {error}</div>
      </div>
    );
  }

  if (!distribution.length) {
    return (
      <div className="mfa-compact-card">
        <div className="mfa-state-message">No data available</div>
      </div>
    );
  }

  return (
    <div className="mfa-compact-card">
      <div className="mfa-header">
        <div>
          <h3 className="mfa-title">MFA Registration by Department</h3>
          <p className="mfa-subtitle">
            {distribution.length} departments · {totalHumanIdentities.toLocaleString()} identities
          </p>
        </div>
        <div className="mfa-overall-badge">
          <span className="mfa-overall-value">{stats.overall}%</span>
          <span className="mfa-overall-label">Overall</span>
        </div>
      </div>

      <div className="mfa-main-content">
  {/* NEW WRAPPER FOR SIDE-BY-SIDE LAYOUT */}
  <div className="mfa-body-wrapper">
    <div className="mfa-chart-section">
      {paginatedData.length > 0 ? (
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      ) : (
        <div className="mfa-state-message">No matches</div>
      )}
    </div>

    <div className="mfa-filters-section">
      <div className="mfa-search-wrapper">
        <svg className="mfa-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="mfa-search-input"
        />
        {searchTerm && (
          <button className="mfa-clear-btn" onClick={() => setSearchTerm("")}>×</button>
        )}
      </div>

      <button
        className="mfa-sort-btn"
        onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
      >
        {sortOrder === "desc" ? "Highest ↓" : "Lowest ↑"}
      </button>

      <div className="mfa-filter-pills">
        <button
          className={`mfa-pill ${filterLevel === "all" ? "active" : ""}`}
          onClick={() => setFilterLevel("all")}
        >
          <span className="mfa-pill-count">{distribution.length}</span>
          <span className="mfa-pill-label">All</span>
        </button>
        <button
          className={`mfa-pill mfa-pill-excellent ${filterLevel === "excellent" ? "active" : ""}`}
          onClick={() => setFilterLevel("excellent")}
          title="≥95%"
        >
          <span className="mfa-pill-count">{stats.byLevel.excellent}</span>
          <span className="mfa-pill-label">Excellent</span>
        </button>
        <button
          className={`mfa-pill mfa-pill-good ${filterLevel === "good" ? "active" : ""}`}
          onClick={() => setFilterLevel("good")}
          title="75-94%"
        >
          <span className="mfa-pill-count">{stats.byLevel.good}</span>
          <span className="mfa-pill-label">Good</span>
        </button>
        <button
          className={`mfa-pill mfa-pill-warning ${filterLevel === "warning" ? "active" : ""}`}
          onClick={() => setFilterLevel("warning")}
          title="40-74%"
        >
          <span className="mfa-pill-count">{stats.byLevel.warning}</span>
          <span className="mfa-pill-label">Warning</span>
        </button>
        <button
          className={`mfa-pill mfa-pill-critical ${filterLevel === "critical" ? "active" : ""}`}
          onClick={() => setFilterLevel("critical")}
          title="<40%"
        >
          <span className="mfa-pill-count">{stats.byLevel.critical}</span>
          <span className="mfa-pill-label">Critical</span>
        </button>
      </div>
    </div>
  </div>

  {/* PAGINATION MOVED HERE (CENTERED UNDER BOTH SECTIONS) */}
  {totalPages > 1 && paginatedData.length > 0 && (
    <div className="mfa-pagination">
      <button
        className="mfa-page-btn"
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
      >
        ←
      </button>
      <span className="mfa-page-info">
        {currentPage} / {totalPages}
      </span>
      <button
        className="mfa-page-btn"
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
      >
        →
      </button>
    </div>
  )}
</div>
    </div>
  );
};

export default MFARegistrationByDepartment;